import { Temporal } from '@js-temporal/polyfill'

import type {
  WorkflowCondition,
  WorkflowConditionGroup,
} from '../types/workflow'
import { calendarDateFromStorage } from './calendar-date'
import { assertWorkflowTimeTokens } from './workflow-time'

export type WorkflowTemporalKind = 'date' | 'datetime'
const DATE_OPERATORS = new Set([
  'before',
  'after',
  'on_or_before',
  'on_or_after',
])
const COMPARISONS = new Set([
  'equals',
  'not_equals',
  'greater_than',
  'greater_or_equal',
  'less_than',
  'less_or_equal',
  ...DATE_OPERATORS,
])
export class WorkflowTemporalConditionError extends RangeError {
  constructor() {
    super(
      'Choose a valid workflow comparison date or an absolute timestamp, and an ordered range. Confirm any clock without a timezone.',
    )
  }
}
export function workflowTemporalKind(
  condition: Pick<WorkflowCondition, 'operator' | 'valueType'>,
): WorkflowTemporalKind | undefined {
  if (condition.valueType === 'date' || condition.valueType === 'datetime')
    return condition.valueType
  // Legacy date operators without field metadata compare instants; never guess
  // that a midnight/noon timestamp represents date-only storage.
  return DATE_OPERATORS.has(condition.operator) ? 'datetime' : undefined
}
const empty = (value: unknown) =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0)
function comparable(
  value: unknown,
  kind: WorkflowTemporalKind,
): string | bigint | null {
  if (empty(value)) return null
  try {
    if (kind === 'date') {
      const day =
        typeof value === 'string' || value instanceof Date
          ? calendarDateFromStorage(value)
          : ''
      if (day) return day
    } else if (value instanceof Date || typeof value === 'number') {
      const milliseconds = value instanceof Date ? value.getTime() : value
      return Temporal.Instant.fromEpochMilliseconds(milliseconds)
        .epochNanoseconds
    } else if (typeof value === 'string') {
      return Temporal.Instant.from(value).epochNanoseconds
    }
  } catch {
    /* Never disclose source field contents in execution diagnostics. */
  }
  throw new WorkflowTemporalConditionError()
}
function operands(operator: string, target: unknown): unknown[] {
  if (operator === 'between' || operator === 'not_between') {
    if (!Array.isArray(target) || target.length !== 2)
      throw new WorkflowTemporalConditionError()
    return target
  }
  if (operator === 'in' || operator === 'not_in') {
    if (Array.isArray(target)) return target
    if (typeof target === 'string')
      return target.split(',').map(part => part.trim())
    throw new WorkflowTemporalConditionError()
  }
  if (!COMPARISONS.has(operator)) throw new WorkflowTemporalConditionError()
  return [target]
}
/** Missing dates require explicit empty predicates. Malformed dates fail the
 * evaluation, including negative predicates, rather than choosing a fallback path. */
export function evaluateWorkflowTemporalCondition(
  condition: WorkflowCondition,
  value: unknown,
  target: unknown,
): boolean {
  const kind = workflowTemporalKind(condition)
  if (!kind) throw new WorkflowTemporalConditionError()
  if (condition.operator === 'is_empty') return empty(value)
  if (condition.operator === 'is_not_empty') return !empty(value)
  const targets = operands(condition.operator, target).map(item =>
    comparable(item, kind),
  )
  if (
    (condition.operator === 'between' ||
      condition.operator === 'not_between') &&
    targets[0] != null &&
    targets[1] != null &&
    targets[0] > targets[1]
  )
    throw new WorkflowTemporalConditionError()
  const left = comparable(value, kind)
  if (
    left === null ||
    targets.length === 0 ||
    targets.some(item => item === null)
  )
    return false
  const right = targets[0]!
  switch (condition.operator) {
    case 'equals':
      return left === right
    case 'not_equals':
      return left !== right
    case 'before':
    case 'less_than':
      return left < right
    case 'after':
    case 'greater_than':
      return left > right
    case 'on_or_before':
    case 'less_or_equal':
      return left <= right
    case 'on_or_after':
    case 'greater_or_equal':
      return left >= right
    case 'in':
      return targets.includes(left)
    case 'not_in':
      return !targets.includes(left)
    case 'between':
    case 'not_between': {
      const max = targets[1]!
      const within = left >= right && left <= max
      return condition.operator === 'between' ? within : !within
    }
    default:
      throw new WorkflowTemporalConditionError()
  }
}
const dynamic = (value: unknown) =>
  typeof value === 'string' &&
  /^{{\s*(?:time\.|payload\.|event\.)[^{}]+}}$/.test(value.trim())
/** Validate static targets before save/activation. Dynamic values are checked
 * again against the actual event during execution. */
export function assertWorkflowTemporalConditions(
  group?: WorkflowConditionGroup | null,
): void {
  if (!group) return
  assertWorkflowTimeTokens(group)
  for (const condition of group.conditions ?? []) {
    const kind = workflowTemporalKind(condition)
    if (
      !kind ||
      condition.operator === 'is_empty' ||
      condition.operator === 'is_not_empty'
    )
      continue
    if (
      !COMPARISONS.has(condition.operator) &&
      !['between', 'not_between', 'in', 'not_in'].includes(condition.operator)
    )
      throw new WorkflowTemporalConditionError()
    if (dynamic(condition.value)) continue
    const values = operands(condition.operator, condition.value).map(value =>
      dynamic(value) ? undefined : comparable(value, kind),
    )
    if (values.length === 0 || values.some(value => value === null))
      throw new WorkflowTemporalConditionError()
    if (
      (condition.operator === 'between' ||
        condition.operator === 'not_between') &&
      values[0] !== undefined &&
      values[1] !== undefined &&
      values[0]! > values[1]!
    )
      throw new WorkflowTemporalConditionError()
  }
  for (const nested of group.groups ?? [])
    assertWorkflowTemporalConditions(nested)
}
